import { useEffect, useState, useCallback } from "react";
import { adminFetch } from "./adminUtils";
import { toast } from "sonner";
import {
  BrainCircuit, Image, BookOpen, TrendingUp, ShieldCheck,
  RefreshCw, Settings2, Loader2, Leaf, AlertTriangle, Zap,
  Store, Coins, Database, PieChart, X, Clock, ChevronRight,
  Search, TrendingDown, Euro, Info, Bell, BellOff, Shield, Unlock,
  Mail, Send,
} from "lucide-react";

const TYPE_LABELS = {
  image_gen: "Imagenes",
  recipe_gen: "Recetas",
  text_gen: "Texto",
  menu_gen: "Menus",
  forecast: "Previsiones",
  enrich: "Enriquecimiento",
};

const TYPE_ICONS = {
  image_gen: Image,
  recipe_gen: BookOpen,
  text_gen: BrainCircuit,
  menu_gen: Zap,
  forecast: TrendingUp,
  enrich: Settings2,
};

const ENTITY_LABELS = {
  dish: "Platos",
  ingredient: "Ingredientes",
  logo: "Logos",
  menu: "Menus",
  recipe: "Recetas",
};

export default function AdminAIUsage() {
  const [stats, setStats] = useState(null);
  const [config, setConfig] = useState(null);
  const [cooldown, setCooldown] = useState(10);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [period, setPeriod] = useState(30);

  const loadData = useCallback(async () => {
    try {
      const [s, c] = await Promise.all([
        adminFetch(`/api/admin/ai-usage?days=${period}`),
        adminFetch("/api/admin/ai-config"),
      ]);
      setStats(s);
      setConfig(c);
      setCooldown(c.batch_cooldown_minutes);
    } catch (e) {
      toast.error("Error cargando datos de IA");
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSaveCooldown = async () => {
    setSavingConfig(true);
    try {
      const r = await adminFetch("/api/admin/ai-config", {
        method: "PUT",
        body: JSON.stringify({ batch_cooldown_minutes: cooldown }),
      });
      setConfig({ batch_cooldown_minutes: r.batch_cooldown_minutes });
      toast.success(`Cooldown actualizado a ${r.batch_cooldown_minutes} min`);
    } catch {
      toast.error("Error guardando configuracion");
    } finally {
      setSavingConfig(false);
    }
  };

  const handleClassifyBeverages = async () => {
    setClassifying(true);
    try {
      const r = await adminFetch("/api/admin/classify-beverages", { method: "POST" });
      toast.success(
        `Clasificadas ${r.beverages_classified} bebidas de ${r.total_scanned} platos. ` +
        `${r.stock_images_assigned} imagenes stock asignadas.`
      );
      loadData();
    } catch {
      toast.error("Error clasificando bebidas");
    } finally {
      setClassifying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
      </div>
    );
  }

  const totalOps = (stats?.total_ai_calls || 0) + (stats?.total_saved_calls || 0);

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="admin-ai-usage">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-violet-500/15 rounded-xl flex items-center justify-center">
            <BrainCircuit className="w-5 h-5 text-violet-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Uso de IA</h1>
            <p className="text-muted-foreground text-xs">Monitoreo de consumo, ahorro y costes</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            data-testid="ai-usage-period-select"
            className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg px-3 py-1.5"
          >
            <option value={7}>7 dias</option>
            <option value={30}>30 dias</option>
            <option value={90}>90 dias</option>
          </select>
          <button onClick={loadData} className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors">
            <RefreshCw className="w-4 h-4 text-slate-400" />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <KPICard label="Llamadas IA" value={stats?.total_ai_calls || 0} icon={BrainCircuit} color="violet" testId="kpi-total-ai" />
        <KPICard label="Ahorradas" value={stats?.total_saved_calls || 0} icon={Leaf} color="emerald" testId="kpi-saved" />
        <KPICard label="% Ahorro" value={`${stats?.savings_percent || 0}%`} icon={ShieldCheck} color="amber" testId="kpi-savings-pct" />
        <KPICard label="Creditos IA" value={Math.round(stats?.total_credits || 0)} icon={Coins} color="blue" testId="kpi-credits" />
        <KPICard label="Fallidas" value={stats?.total_failed || 0} icon={AlertTriangle} color="red" testId="kpi-failed" />
      </div>

      {/* Today's stats */}
      {stats?.today_total > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 mb-5">
          <h3 className="text-slate-300 text-sm font-semibold mb-2">Hoy</h3>
          <div className="flex flex-wrap gap-4">
            {Object.entries(stats.today).map(([type, count]) => (
              <div key={type} className="flex items-center gap-2 text-xs">
                <span className="text-muted-foreground">{TYPE_LABELS[type] || type}:</span>
                <span className="text-white font-medium">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Margen IA en tiempo real — Emergent cost vs MenuGenius revenue */}
      <MarginBoard period={period} />

      {/* Margen IA — alert config */}
      <MarginAlertsPanel />

      {/* Auto-throttle de endpoints IA caros */}
      <ThrottlePanel />

      {/* Weekly digest email toggle + send-now */}
      <WeeklyDigestPanel />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
        {/* Breakdown by type */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="text-slate-300 text-sm font-semibold mb-4 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-muted-foreground" />
            Desglose por tipo
          </h3>
          {Object.keys(stats?.by_type || {}).length === 0 ? (
            <p className="text-muted-foreground text-xs">Sin datos en el periodo seleccionado</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.by_type)
                .sort(([,a], [,b]) => (b.total_credits || 0) - (a.total_credits || 0))
                .map(([type, data]) => {
                const Icon = TYPE_ICONS[type] || BrainCircuit;
                const total = data.ai_calls + data.saved_calls;
                const pct = total > 0 ? Math.round((data.ai_calls / total) * 100) : 0;
                return (
                  <div key={type} className="flex items-center gap-3" data-testid={`ai-type-${type}`}>
                    <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon className="w-4 h-4 text-violet-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-slate-300 text-xs font-medium">{TYPE_LABELS[type] || type}</span>
                        <span className="text-muted-foreground text-[10px]">
                          {data.ai_calls} IA + {data.saved_calls} stock
                          {data.total_credits > 0 && <span className="text-violet-400 ml-1">({Math.round(data.total_credits)} cr)</span>}
                          {data.failed > 0 && <span className="text-red-400 ml-1">({data.failed} err)</span>}
                        </span>
                      </div>
                      <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-violet-500 to-violet-400 rounded-full transition-all"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* By entity type */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="text-slate-300 text-sm font-semibold mb-4 flex items-center gap-2">
            <Database className="w-4 h-4 text-muted-foreground" />
            Por tipo de entidad
          </h3>
          {Object.keys(stats?.by_entity || {}).length === 0 ? (
            <p className="text-muted-foreground text-xs">Sin datos de entidad en el periodo</p>
          ) : (
            <div className="space-y-3">
              {Object.entries(stats.by_entity)
                .sort(([,a], [,b]) => (b.credits || 0) - (a.credits || 0))
                .map(([etype, data]) => {
                const total = data.ai_calls + data.saved_calls;
                const savePct = total > 0 ? Math.round((data.saved_calls / total) * 100) : 0;
                return (
                  <div key={etype} className="bg-slate-800/50 rounded-lg p-3" data-testid={`entity-${etype}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-slate-300 text-xs font-medium">{ENTITY_LABELS[etype] || etype}</span>
                      <span className="text-muted-foreground text-[10px]">
                        {data.ai_calls} IA / {data.saved_calls} stock
                      </span>
                    </div>
                    <div className="flex items-center gap-3 text-[10px]">
                      {data.credits > 0 && (
                        <span className="text-violet-400">{Math.round(data.credits)} creditos</span>
                      )}
                      {savePct > 0 && (
                        <span className="text-emerald-400">{savePct}% ahorro</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Cache hits */}
          {Object.keys(stats?.cache_hits || {}).length > 0 && (
            <div className="mt-4 pt-3 border-t border-slate-800">
              <h4 className="text-slate-400 text-[10px] uppercase tracking-wider mb-2">Cache hits</h4>
              <div className="flex flex-wrap gap-2">
                {Object.entries(stats.cache_hits).map(([type, count]) => (
                  <span key={type} className="bg-emerald-500/10 text-emerald-400 text-[10px] px-2 py-1 rounded-full">
                    {TYPE_LABELS[type] || type}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* All Restaurants Table */}
      <RestaurantAITable period={period} />

      {/* Configuration */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Cooldown Config */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="text-slate-300 text-sm font-semibold mb-4 flex items-center gap-2">
            <Settings2 className="w-4 h-4 text-muted-foreground" />
            Cooldown entre lotes
          </h3>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={1}
              max={60}
              value={cooldown}
              onChange={(e) => setCooldown(Number(e.target.value))}
              data-testid="cooldown-input"
              className="w-20 bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2"
            />
            <span className="text-muted-foreground text-xs">minutos</span>
            <button
              onClick={handleSaveCooldown}
              disabled={savingConfig || cooldown === config?.batch_cooldown_minutes}
              data-testid="save-cooldown-btn"
              className="ml-auto bg-violet-600 hover:bg-violet-500 disabled:bg-slate-700 disabled:text-slate-500
                         text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors"
            >
              {savingConfig ? "Guardando..." : "Guardar"}
            </button>
          </div>
          <p className="text-muted-foreground text-[10px] mt-2">
            Tiempo minimo entre generaciones masivas de imagenes o recetas por restaurante.
          </p>
        </div>

        {/* Classify beverages action */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <h3 className="text-slate-300 text-sm font-semibold mb-4 flex items-center gap-2">
            <Leaf className="w-4 h-4 text-emerald-500" />
            Clasificar bebidas
          </h3>
          <p className="text-muted-foreground text-xs mb-4">
            Escanea todos los platos y marca automaticamente los que son bebidas.
            Las bebidas reciben imagenes stock gratuitas en lugar de IA.
          </p>
          <button
            onClick={handleClassifyBeverages}
            disabled={classifying}
            data-testid="classify-beverages-btn"
            className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500
                       text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors flex items-center gap-2"
          >
            {classifying ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Clasificando...</>
            ) : (
              "Ejecutar clasificacion"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function KPICard({ label, value, icon: Icon, color, testId }) {
  const colorMap = {
    violet: "bg-violet-500/10 text-violet-400 border-violet-500/20",
    emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
  };
  const iconColorMap = {
    violet: "text-violet-400",
    emerald: "text-emerald-400",
    amber: "text-amber-400",
    red: "text-red-400",
    blue: "text-blue-400",
  };
  return (
    <div className={`border rounded-xl p-3 sm:p-4 ${colorMap[color]}`} data-testid={testId}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className={`w-3.5 h-3.5 ${iconColorMap[color]}`} />
        <span className="text-muted-foreground text-[10px] uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-xl sm:text-2xl font-bold text-white">{value}</p>
    </div>
  );
}

// ── Restaurant AI Table ─────────────────────────────────────────────────
function RestaurantAITable({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedRest, setSelectedRest] = useState(null);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin/ai-usage/restaurants?days=${period}`)
      .then(setData)
      .catch(() => toast.error("Error cargando tabla de restaurantes"))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-violet-400 animate-spin" /></div>;
  if (!data?.restaurants?.length) return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-8 mb-5 text-center">
      <Store className="w-8 h-8 text-foreground mx-auto mb-2" />
      <p className="text-muted-foreground text-sm">No hay datos de uso IA por restaurante en este periodo</p>
    </div>
  );

  const filtered = search.trim()
    ? data.restaurants.filter(r => r.name.toLowerCase().includes(search.toLowerCase()))
    : data.restaurants;

  return (
    <>
      <div className="bg-slate-900 border border-slate-800 rounded-xl mb-5 overflow-hidden" data-testid="restaurant-ai-table">
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between gap-4">
          <h3 className="text-slate-300 text-sm font-semibold flex items-center gap-2">
            <Store className="w-4 h-4 text-muted-foreground" />
            Consumo IA por restaurante ({data.total_restaurants})
          </h3>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar restaurante..."
              className="bg-slate-800 border border-slate-700 text-slate-300 text-xs rounded-lg pl-8 pr-3 py-1.5 w-48"
              data-testid="restaurant-ai-search"
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-800/50 text-[10px] text-muted-foreground uppercase tracking-wider">
                <th className="text-left px-4 py-2.5">Restaurante</th>
                <th className="text-center px-3 py-2.5">Recetas</th>
                <th className="text-center px-3 py-2.5">Imagenes</th>
                <th className="text-center px-3 py-2.5">Otros</th>
                <th className="text-center px-3 py-2.5">Total</th>
                <th className="text-center px-3 py-2.5">Creditos</th>
                <th className="text-center px-3 py-2.5">T. Total</th>
                <th className="text-center px-3 py-2.5">T. Medio</th>
                <th className="text-center px-3 py-2.5">Workers</th>
                <th className="text-center px-3 py-2.5">Fallos</th>
                <th className="text-center px-3 py-2.5">Ultima</th>
                <th className="px-3 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => (
                <tr
                  key={r.restaurant_id}
                  className={`border-t border-slate-800/50 hover:bg-slate-800/30 cursor-pointer transition-colors ${i % 2 === 1 ? "bg-slate-900/50" : ""}`}
                  onClick={() => setSelectedRest(r.restaurant_id)}
                  data-testid={`restaurant-row-${r.restaurant_id}`}
                >
                  <td className="px-4 py-3 text-slate-300 text-xs font-medium truncate max-w-[180px]">{r.name}</td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-blue-400 font-medium">{r.recipe_calls}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-violet-400 font-medium">{r.image_calls}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-muted-foreground">{r.other_calls}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-white font-bold">{r.total_calls}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-amber-400 font-semibold">{r.total_credits}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-cyan-400">{r.total_duration_min}m</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-slate-400">{r.avg_duration_s}s</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <span className="text-xs text-violet-400 font-medium">{r.workers_activated || 0}</span>
                  </td>
                  <td className="px-3 py-3 text-center">
                    {r.failures > 0 ? (
                      <span className="text-xs text-red-400 font-medium">{r.failures}</span>
                    ) : (
                      <span className="text-xs text-foreground">0</span>
                    )}
                  </td>
                  <td className="px-3 py-3 text-center text-[10px] text-muted-foreground">
                    {r.last_call ? new Date(r.last_call).toLocaleDateString("es-ES", { day: "2-digit", month: "short" }) : "-"}
                  </td>
                  <td className="px-3 py-3">
                    <ChevronRight className="w-3.5 h-3.5 text-foreground" />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail modal */}
      {selectedRest && (
        <RestaurantDetailModal
          restaurantId={selectedRest}
          period={period}
          onClose={() => setSelectedRest(null)}
        />
      )}
    </>
  );
}

// ── Restaurant Detail Modal ─────────────────────────────────────────────
function RestaurantDetailModal({ restaurantId, period, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin/ai-usage/restaurant/${restaurantId}?days=${period}`)
      .then(setData)
      .catch(() => toast.error("Error cargando detalle"))
      .finally(() => setLoading(false));
  }, [restaurantId, period]);

  const fmtDate = (ts) => {
    if (!ts) return "-";
    return new Date(ts).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="restaurant-detail-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
          <div>
            <h2 className="text-white text-lg font-bold">{data?.restaurant_name || "Cargando..."}</h2>
            {data?.cuisine_type && <p className="text-muted-foreground text-xs mt-0.5 capitalize">{data.cuisine_type}</p>}
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-violet-400 animate-spin" />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 space-y-5">
            {/* KPIs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MiniKPI label="Llamadas IA" value={data.totals.ai_calls} color="text-violet-400" />
              <MiniKPI label="Creditos" value={data.totals.total_credits} color="text-amber-400" />
              <MiniKPI label="Tiempo medio" value={`${data.totals.avg_duration_s}s`} sub={`(${data.totals.avg_duration_min} min)`} color="text-blue-400" />
              <MiniKPI label="Fallos" value={data.totals.failed} color={data.totals.failed > 0 ? "text-red-400" : "text-muted-foreground"} />
            </div>

            {/* Time totals + workers */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <MiniKPI label="Tiempo total" value={`${data.totals.total_duration_min || 0}m`} sub={`${data.totals.total_duration_s || 0}s`} color="text-cyan-400" />
              <MiniKPI label="T. Imagenes" value={`${data.totals.duration_images_min || 0}m`} sub={`${data.totals.duration_images_s || 0}s`} color="text-violet-400" />
              <MiniKPI label="T. Recetas" value={`${data.totals.duration_recipes_min || 0}m`} sub={`${data.totals.duration_recipes_s || 0}s`} color="text-blue-400" />
              <MiniKPI label="Workers activados" value={data.workers?.total_batches || 0} sub={`${data.workers?.recipe_batches || 0} rec / ${data.workers?.image_batches || 0} img`} color="text-emerald-400" />
            </div>

            {/* By Type */}
            <div>
              <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Desglose por tipo</h3>
              {Object.keys(data.by_type).length === 0 ? (
                <p className="text-muted-foreground text-xs">Sin llamadas IA en este periodo</p>
              ) : (
                <div className="space-y-2">
                  {Object.entries(data.by_type).map(([type, d]) => {
                    const Icon = TYPE_ICONS[type] || BrainCircuit;
                    return (
                      <div key={type} className="bg-slate-800/50 rounded-lg p-3 flex items-center gap-3">
                        <Icon className="w-4 h-4 text-violet-400 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="text-slate-300 text-xs font-medium">{TYPE_LABELS[type] || type}</span>
                        </div>
                        <div className="flex items-center gap-4 text-[10px]">
                          <span className="text-slate-400">{d.ai_calls} llamadas</span>
                          <span className="text-amber-400">{d.credits} cr</span>
                          <span className="text-blue-400 flex items-center gap-1">
                            <Clock className="w-3 h-3" /> {d.avg_duration_s}s
                          </span>
                          {d.failed > 0 && <span className="text-red-400">{d.failed} fallos</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Recent Calls */}
            {data.recent_calls?.length > 0 && (
              <div>
                <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Llamadas recientes</h3>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {data.recent_calls.map((c, i) => (
                    <div key={`detail-${i}-${item?.label || item?.name || i}`} className="flex items-center gap-2 text-[10px] py-1.5 px-2 rounded bg-slate-800/30">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.success ? "bg-emerald-400" : "bg-red-400"}`} />
                      <span className="text-muted-foreground w-20 flex-shrink-0">{fmtDate(c.timestamp)}</span>
                      <span className="text-violet-400 w-20 flex-shrink-0">{TYPE_LABELS[c.call_type] || c.call_type}</span>
                      <span className="text-slate-300 flex-1 truncate">{c.dish_name || "-"}</span>
                      <span className="text-muted-foreground w-14 text-right">{c.duration_s?.toFixed(1)}s</span>
                      <span className="text-amber-400/70 w-10 text-right">{c.estimated_credits?.toFixed(1) || "0"} cr</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Credit Transactions */}
            {data.credit_transactions?.length > 0 && (
              <div>
                <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">Transacciones de creditos</h3>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {data.credit_transactions.map((t, i) => (
                    <div key={`detail2-${i}-${item?.label || item?.name || i}`} className="flex items-center gap-2 text-[10px] py-1.5 px-2 rounded bg-slate-800/30">
                      <span className="text-muted-foreground w-20 flex-shrink-0">{fmtDate(t.timestamp)}</span>
                      <span className="text-slate-300 flex-1 truncate">{t.details || t.action}</span>
                      <span className={`w-16 text-right font-medium ${t.credits_used > 0 ? "text-red-400" : "text-emerald-400"}`}>
                        {t.credits_used > 0 ? `-${t.credits_used}` : `+${Math.abs(t.credits_used)}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Workers / Batches */}
            {data.workers?.batches?.length > 0 && (
              <div>
                <h3 className="text-slate-400 text-xs font-semibold uppercase tracking-wider mb-3">
                  Workers activados ({data.workers.total_batches} lotes)
                </h3>
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {data.workers.batches.map((b, i) => {
                    const duration = b.completed_at && b.created_at
                      ? Math.round((new Date(b.completed_at) - new Date(b.created_at)) / 1000)
                      : null;
                    return (
                      <div key={`worker-${i}-${b?.batch_id || i}`} className="flex items-center gap-2 text-[10px] py-2 px-3 rounded bg-slate-800/30">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${b.status === "completed" ? "bg-emerald-400" : b.status === "running" ? "bg-amber-400 animate-pulse" : "bg-slate-600"}`} />
                        <span className="text-muted-foreground w-20 flex-shrink-0">{fmtDate(b.created_at)}</span>
                        <span className={`w-16 flex-shrink-0 font-medium ${b.type === "images" ? "text-violet-400" : "text-blue-400"}`}>
                          {b.type === "images" ? "Imagenes" : "Recetas"}
                        </span>
                        <span className="text-slate-300 flex-1">
                          {b.done}/{b.total} completados
                          {b.failed > 0 && <span className="text-red-400 ml-1">({b.failed} fallos)</span>}
                        </span>
                        {duration !== null && (
                          <span className="text-cyan-400 w-16 text-right">
                            {duration >= 60 ? `${Math.round(duration / 60)}m ${duration % 60}s` : `${duration}s`}
                          </span>
                        )}
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                          b.status === "completed" ? "bg-emerald-500/15 text-emerald-400" :
                          b.status === "running" ? "bg-amber-500/15 text-amber-400" :
                          "bg-slate-700 text-muted-foreground"
                        }`}>{b.status}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MiniKPI({ label, value, sub, color = "text-white" }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-3 text-center">
      <p className="text-muted-foreground text-[10px] uppercase tracking-wider mb-1">{label}</p>
      <p className={`text-lg font-bold ${color}`}>{value}</p>
      {sub && <p className="text-muted-foreground text-[9px]">{sub}</p>}
    </div>
  );
}

// ── Margen IA en tiempo real ───────────────────────────────────────────
function MarginBoard({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    setLoading(true);
    adminFetch(`/api/admin/ai-usage/margin?days=${period}`)
      .then(setData)
      .catch(() => toast.error("Error cargando margen IA"))
      .finally(() => setLoading(false));
  }, [period]);

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5 flex items-center gap-3">
        <Loader2 className="w-4 h-4 text-violet-400 animate-spin" />
        <span className="text-muted-foreground text-xs">Calculando margen IA…</span>
      </div>
    );
  }
  if (!data) return null;

  const t = data.totals;
  const marginPositive = t.margin_eur >= 0;
  const marginColor = marginPositive ? "text-emerald-400" : "text-red-400";
  const marginBg = marginPositive
    ? "from-emerald-500/10 to-emerald-900/5 border-emerald-500/20"
    : "from-red-500/10 to-red-900/5 border-red-500/20";

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5" data-testid="ai-margin-board">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-500/15 rounded-lg flex items-center justify-center">
            <Euro className="w-4 h-4 text-amber-400" />
          </div>
          <div>
            <h3 className="text-white text-sm font-bold flex items-center gap-2">
              Margen IA en tiempo real
              <button
                onClick={() => setShowHelp(!showHelp)}
                className="p-1 hover:bg-slate-800 rounded transition-colors"
                data-testid="margin-help-btn"
              >
                <Info className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </h3>
            <p className="text-muted-foreground text-[10px]">
              Coste Emergent (estimado) vs revenue en créditos MG — últimos {data.period_days} días
            </p>
          </div>
        </div>
      </div>

      {/* Help panel */}
      {showHelp && (
        <div className="bg-slate-800/40 border border-slate-700 rounded-lg p-3 mb-4 text-[11px] text-slate-400 space-y-1.5">
          <p>{data.assumptions?.note}</p>
          <p>
            <strong className="text-slate-300">1 crédito MG</strong> = {data.assumptions?.revenue_per_credit_eur} €
            (blended Pro). Costes por modelo en hipótesis:
          </p>
          <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[10px] font-mono">
            {Object.entries(data.assumptions?.model_cost_eur || {}).slice(0, 10).map(([m, c]) => (
              <span key={m} className="text-muted-foreground">
                {m}: <span className="text-amber-400">{c}€</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Big KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-slate-800/50 rounded-lg p-3" data-testid="margin-kpi-cost">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingDown className="w-3 h-3 text-red-400" />
            <span className="text-muted-foreground text-[10px] uppercase tracking-wider">Coste Emergent</span>
          </div>
          <p className="text-xl font-bold text-red-400">{t.cost_eur.toFixed(2)}€</p>
          <p className="text-muted-foreground text-[9px]">{t.ai_calls} llamadas IA</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3" data-testid="margin-kpi-revenue">
          <div className="flex items-center gap-1.5 mb-1">
            <Coins className="w-3 h-3 text-amber-400" />
            <span className="text-muted-foreground text-[10px] uppercase tracking-wider">Revenue</span>
          </div>
          <p className="text-xl font-bold text-amber-400">{t.revenue_eur.toFixed(2)}€</p>
          <p className="text-muted-foreground text-[9px]">{t.revenue_credits} cr cobrados</p>
        </div>
        <div className={`bg-gradient-to-br ${marginBg} border rounded-lg p-3`} data-testid="margin-kpi-margin">
          <div className="flex items-center gap-1.5 mb-1">
            <TrendingUp className={`w-3 h-3 ${marginColor}`} />
            <span className="text-muted-foreground text-[10px] uppercase tracking-wider">Margen</span>
          </div>
          <p className={`text-xl font-bold ${marginColor}`}>
            {marginPositive ? "+" : ""}{t.margin_eur.toFixed(2)}€
          </p>
          <p className="text-muted-foreground text-[9px]">{t.margin_pct}% sobre revenue</p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3" data-testid="margin-kpi-orphan">
          <div className="flex items-center gap-1.5 mb-1">
            <AlertTriangle className="w-3 h-3 text-orange-400" />
            <span className="text-muted-foreground text-[10px] uppercase tracking-wider">Huérfano</span>
          </div>
          <p className="text-xl font-bold text-orange-400">{data.system_orphan.cost_eur.toFixed(3)}€</p>
          <p className="text-muted-foreground text-[9px]">{data.system_orphan.ai_calls} sin restaurante</p>
        </div>
      </div>

      {/* Top / worst restaurants */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MarginList
          title="Top margen"
          rows={data.best_margin}
          icon={TrendingUp}
          iconColor="text-emerald-400"
          testId="margin-best-list"
        />
        <MarginList
          title="Peor margen / pérdida"
          rows={data.worst_margin}
          icon={TrendingDown}
          iconColor="text-red-400"
          testId="margin-worst-list"
        />
      </div>
    </div>
  );
}

function MarginList({ title, rows, icon: Icon, iconColor, testId }) {
  if (!rows?.length) {
    return (
      <div className="bg-slate-800/30 rounded-lg p-4">
        <h4 className="text-slate-400 text-xs font-semibold mb-2 flex items-center gap-2">
          <Icon className={`w-3.5 h-3.5 ${iconColor}`} /> {title}
        </h4>
        <p className="text-muted-foreground text-[10px]">Sin datos en el periodo</p>
      </div>
    );
  }
  return (
    <div className="bg-slate-800/30 rounded-lg p-3" data-testid={testId}>
      <h4 className="text-slate-400 text-xs font-semibold mb-2 flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${iconColor}`} /> {title}
      </h4>
      <div className="space-y-1">
        {rows.map((r) => {
          const positive = r.margin_eur >= 0;
          return (
            <div
              key={r.restaurant_id}
              className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded bg-slate-900/40"
            >
              <span className="text-slate-300 flex-1 truncate">{r.name}</span>
              <span className="text-muted-foreground w-14 text-right">{r.ai_calls} IA</span>
              <span className="text-red-400/70 w-14 text-right">-{r.cost_eur.toFixed(2)}€</span>
              <span className="text-amber-400/70 w-14 text-right">+{r.revenue_eur.toFixed(2)}€</span>
              <span className={`w-16 text-right font-semibold ${positive ? "text-emerald-400" : "text-red-400"}`}>
                {positive ? "+" : ""}{r.margin_eur.toFixed(2)}€
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── MarginAlertsPanel: config + history for IA margin alerts ────────────
function MarginAlertsPanel() {
  const [config, setConfig] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [c, h] = await Promise.all([
        adminFetch("/api/admin/ai-usage/margin-alerts/config"),
        adminFetch("/api/admin/ai-usage/margin-alerts/history?limit=10"),
      ]);
      setConfig(c);
      setHistory(h.alerts || []);
    } catch {
      toast.error("Error cargando alertas IA");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const updateField = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await adminFetch("/api/admin/ai-usage/margin-alerts/config", {
        method: "PUT",
        body: JSON.stringify({
          enabled: config.enabled,
          min_cost_eur: Number(config.min_cost_eur),
          min_margin_pct: Number(config.min_margin_pct),
        }),
      });
      setConfig(saved);
      toast.success("Configuración guardada");
    } catch {
      toast.error("Error guardando config");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5 flex items-center gap-3">
        <Loader2 className="w-4 h-4 text-amber-400 animate-spin" />
        <span className="text-muted-foreground text-xs">Cargando alertas de margen IA…</span>
      </div>
    );
  }
  if (!config) return null;

  const enabled = !!config.enabled;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5" data-testid="margin-alerts-panel">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? "bg-amber-500/15" : "bg-slate-800"}`}>
            {enabled
              ? <Bell className="w-4 h-4 text-amber-400" />
              : <BellOff className="w-4 h-4 text-muted-foreground" />}
          </div>
          <div>
            <h3 className="text-white text-sm font-bold">Alertas automáticas de margen IA</h3>
            <p className="text-muted-foreground text-[10px]">
              Cada 6h cruza coste Emergent 24h × revenue. Si un restaurante
              cruza los umbrales, se envía email a los superadmins.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-margin-alerts">
          <span className="text-slate-400 text-xs">{enabled ? "Activas" : "Desactivadas"}</span>
          <button
            onClick={() => updateField("enabled", !enabled)}
            className={`relative w-9 h-5 rounded-full transition-colors ${enabled ? "bg-amber-500" : "bg-slate-700"}`}
            aria-pressed={enabled}
          >
            <span
              className={`absolute top-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
            />
          </button>
        </label>
      </div>

      {/* Thresholds */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-slate-800/50 rounded-lg p-3">
          <label className="text-slate-400 text-[10px] uppercase tracking-wider block mb-1.5">
            Coste mínimo para alertar (24h)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="0.1"
              min="0"
              value={config.min_cost_eur}
              onChange={(e) => updateField("min_cost_eur", e.target.value)}
              disabled={!enabled}
              data-testid="margin-min-cost-input"
              className="flex-1 bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 disabled:opacity-50"
            />
            <span className="text-muted-foreground text-xs">€</span>
          </div>
          <p className="text-muted-foreground text-[10px] mt-1">
            Restaurantes con menos coste se ignoran.
          </p>
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <label className="text-slate-400 text-[10px] uppercase tracking-wider block mb-1.5">
            % margen mínimo aceptable
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              step="1"
              min="0"
              max="100"
              value={config.min_margin_pct}
              onChange={(e) => updateField("min_margin_pct", e.target.value)}
              disabled={!enabled}
              data-testid="margin-min-pct-input"
              className="flex-1 bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 disabled:opacity-50"
            />
            <span className="text-muted-foreground text-xs">%</span>
          </div>
          <p className="text-muted-foreground text-[10px] mt-1">
            Se alerta cuando margen baja de este umbral.
          </p>
        </div>
        <div className="flex items-end">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-margin-alerts-btn"
            className="w-full bg-amber-600 hover:bg-amber-500 disabled:bg-slate-700 disabled:text-slate-500
                       text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</> : "Guardar configuración"}
          </button>
        </div>
      </div>

      {/* History */}
      <div>
        <h4 className="text-slate-400 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-2">
          <AlertTriangle className="w-3 h-3" />
          Últimas {history.length} alertas disparadas
        </h4>
        {history.length === 0 ? (
          <div className="bg-slate-800/30 rounded-lg p-4 text-center">
            <p className="text-muted-foreground text-xs">Sin alertas en el histórico reciente.</p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-60 overflow-y-auto" data-testid="margin-alerts-history">
            {history.map((a) => {
              const p = a.payload || {};
              const isCritical = a.severity === "critical";
              return (
                <div
                  key={a.alert_id}
                  className={`flex items-center gap-2 text-[11px] py-2 px-3 rounded-lg border ${
                    isCritical
                      ? "bg-red-500/5 border-red-500/20"
                      : "bg-amber-500/5 border-amber-500/20"
                  }`}
                >
                  <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${isCritical ? "bg-red-400" : "bg-amber-400"}`} />
                  <span className="text-slate-400 w-24 flex-shrink-0">
                    {new Date(a.created_at).toLocaleString("es-ES", {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    })}
                  </span>
                  <span className="text-slate-200 font-medium flex-1 truncate">{a.label}</span>
                  <span className="text-red-400/80 w-14 text-right">-{p.cost_eur?.toFixed(2) ?? "0.00"}€</span>
                  <span className="text-amber-400/80 w-14 text-right">+{p.revenue_eur?.toFixed(2) ?? "0.00"}€</span>
                  <span className={`w-16 text-right font-bold ${isCritical ? "text-red-400" : "text-amber-400"}`}>
                    {(p.margin_eur ?? 0).toFixed(2)}€
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


// ── ThrottlePanel: config + active throttles for expensive IA endpoints ──
function ThrottlePanel() {
  const [config, setConfig] = useState(null);
  const [active, setActive] = useState([]);
  const [funnel, setFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [releasing, setReleasing] = useState(null);

  const load = async () => {
    try {
      const [c, a, f] = await Promise.all([
        adminFetch("/api/admin/ai-usage/throttle/config"),
        adminFetch("/api/admin/ai-usage/throttle/active"),
        adminFetch("/api/admin/ai-usage/throttle/funnel?days=30"),
      ]);
      setConfig(c);
      setActive(a.throttles || []);
      setFunnel(f);
    } catch {
      toast.error("Error cargando auto-throttle");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const updateField = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const saved = await adminFetch("/api/admin/ai-usage/throttle/config", {
        method: "PUT",
        body: JSON.stringify({
          enabled: config.enabled,
          strikes_required: Number(config.strikes_required),
          window_days: Number(config.window_days),
          expires_after_days: Number(config.expires_after_days),
        }),
      });
      setConfig(saved);
      toast.success("Configuración de auto-throttle guardada");
    } catch {
      toast.error("Error guardando config");
    } finally {
      setSaving(false);
    }
  };

  const handleRelease = async (rid, name) => {
    if (!window.confirm(`¿Liberar el auto-throttle de "${name}"?\nVolverá a poder usar las funciones IA caras.`)) return;
    setReleasing(rid);
    try {
      await adminFetch(`/api/admin/ai-usage/throttle/${rid}/release`, {
        method: "POST",
        body: JSON.stringify({ reason: "Liberado manualmente desde panel admin" }),
      });
      toast.success(`Throttle liberado para ${name}`);
      await load();
    } catch {
      toast.error("Error liberando throttle");
    } finally {
      setReleasing(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5 flex items-center gap-3">
        <Loader2 className="w-4 h-4 text-rose-400 animate-spin" />
        <span className="text-muted-foreground text-xs">Cargando auto-throttle…</span>
      </div>
    );
  }
  if (!config) return null;

  const enabled = !!config.enabled;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5" data-testid="throttle-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? "bg-rose-500/15" : "bg-slate-800"}`}>
            <Shield className={`w-4 h-4 ${enabled ? "text-rose-400" : "text-muted-foreground"}`} />
          </div>
          <div>
            <h3 className="text-white text-sm font-bold">Auto-throttle de endpoints IA caros</h3>
            <p className="text-muted-foreground text-[10px]">
              Si un restaurante acumula N alertas críticas de margen en una
              ventana de días, se bloquean automáticamente los endpoints Claude
              (analytics/ask, analytics/ai-insights, finance/insights refresh) devolviendo HTTP 429.
            </p>
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-throttle">
          <span className="text-slate-400 text-xs">{enabled ? "Activo" : "Desactivado"}</span>
          <button
            onClick={() => updateField("enabled", !enabled)}
            className={`relative w-9 h-5 rounded-full transition-colors ${enabled ? "bg-rose-500" : "bg-slate-700"}`}
            aria-pressed={enabled}
          >
            <span
              className={`absolute top-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
            />
          </button>
        </label>
      </div>

      {/* Thresholds */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-slate-800/50 rounded-lg p-3">
          <label className="text-slate-400 text-[10px] uppercase tracking-wider block mb-1.5">
            Strikes requeridos
          </label>
          <input
            type="number" min="1" max="10"
            value={config.strikes_required}
            onChange={(e) => updateField("strikes_required", e.target.value)}
            disabled={!enabled}
            data-testid="throttle-strikes-input"
            className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 disabled:opacity-50"
          />
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <label className="text-slate-400 text-[10px] uppercase tracking-wider block mb-1.5">
            Ventana (días)
          </label>
          <input
            type="number" min="1" max="90"
            value={config.window_days}
            onChange={(e) => updateField("window_days", e.target.value)}
            disabled={!enabled}
            data-testid="throttle-window-input"
            className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 disabled:opacity-50"
          />
        </div>
        <div className="bg-slate-800/50 rounded-lg p-3">
          <label className="text-slate-400 text-[10px] uppercase tracking-wider block mb-1.5">
            Auto-expira (días)
          </label>
          <input
            type="number" min="1" max="90"
            value={config.expires_after_days}
            onChange={(e) => updateField("expires_after_days", e.target.value)}
            disabled={!enabled}
            data-testid="throttle-expires-input"
            className="w-full bg-slate-900 border border-slate-700 text-white text-sm rounded-lg px-3 py-1.5 disabled:opacity-50"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="save-throttle-btn"
            className="w-full bg-rose-600 hover:bg-rose-500 disabled:bg-slate-700 disabled:text-slate-500
                       text-white text-xs font-medium px-4 py-2 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</> : "Guardar"}
          </button>
        </div>
      </div>

      {/* Funnel: impressions → conversions (últimos 30d) */}
      {funnel && funnel.throttles_created + funnel.impressions > 0 && (
        <div className="mb-4" data-testid="throttle-funnel">
          <h4 className="text-slate-400 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-2">
            <TrendingUp className="w-3 h-3" />
            Embudo de conversión · últimos {funnel.period_days} días
          </h4>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-2">
            <FunnelTile
              label="Throttles creados"
              value={funnel.throttles_created}
              sub={`${funnel.unique_restaurants_throttled} rest.`}
              color="text-rose-400"
            />
            <FunnelTile
              label="429 servidos"
              value={funnel.impressions}
              sub="impresiones"
              color="text-orange-400"
            />
            <FunnelTile
              label="→ Upgrades"
              value={funnel.conversion_upgrade}
              sub="plan"
              color="text-emerald-400"
            />
            <FunnelTile
              label="→ Top-ups"
              value={funnel.conversion_topup}
              sub="créditos"
              color="text-emerald-400"
            />
            <FunnelTile
              label="Conversion rate"
              value={`${funnel.conversion_rate_pct}%`}
              sub={`${funnel.conversions} de ${funnel.conversions + funnel.releases_manual + funnel.releases_expired} salidas`}
              color={
                funnel.conversion_rate_pct >= 40
                  ? "text-emerald-400"
                  : funnel.conversion_rate_pct >= 20
                  ? "text-amber-400"
                  : "text-rose-400"
              }
            />
          </div>

          {/* Speed-to-conversion row */}
          {funnel.conversions_with_timing > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2" data-testid="throttle-ttc">
              <FunnelTile
                label="Time-to-conversion (median)"
                value={formatTTC(funnel.time_to_conversion_median_hours)}
                sub={`${funnel.conversions_with_timing} pares 429→pago`}
                color={
                  funnel.time_to_conversion_median_hours == null
                    ? "text-slate-400"
                    : funnel.time_to_conversion_median_hours <= 1
                    ? "text-emerald-400"
                    : funnel.time_to_conversion_median_hours <= 24
                    ? "text-amber-400"
                    : "text-rose-400"
                }
              />
              <FunnelTile
                label="Instant (<1h)"
                value={`${funnel.time_to_conversion_fast_pct ?? 0}%`}
                sub="convierten en toast"
                color={
                  (funnel.time_to_conversion_fast_pct ?? 0) >= 50
                    ? "text-emerald-400"
                    : (funnel.time_to_conversion_fast_pct ?? 0) >= 25
                    ? "text-amber-400"
                    : "text-slate-400"
                }
              />
              <div className="sm:col-span-2 bg-slate-800/30 rounded-lg p-2.5 text-[11px] text-slate-400 flex items-start gap-2">
                <Info className="w-3 h-3 mt-0.5 text-muted-foreground flex-shrink-0" />
                <p>
                  Si <strong className="text-slate-200">Instant</strong> baja de
                  25% y el median supera las 24h, el toast no está convirtiendo
                  bien — revisa copy, CTAs o thresholds.
                </p>
              </div>
            </div>
          )}
          {funnel.breakdown?.length > 0 && (
            <details className="bg-slate-800/30 rounded-lg p-2">
              <summary className="text-slate-400 text-[10px] cursor-pointer hover:text-slate-200 py-1 px-1">
                Top {funnel.breakdown.length} restaurantes por impresiones
              </summary>
              <div className="mt-2 space-y-1">
                {funnel.breakdown.map((r) => (
                  <div
                    key={r.restaurant_id}
                    className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded bg-slate-900/40"
                  >
                    <span className="text-slate-300 flex-1 truncate">{r.name}</span>
                    <span className="text-orange-400/80 w-14 text-right">{r.impressions} 429</span>
                    <span className="text-rose-400/80 w-14 text-right">{r.throttles_created} blk</span>
                    <span className="text-emerald-400/80 w-14 text-right">{r.conversions} conv</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      )}

      {/* Active throttles */}
      <div>
        <h4 className="text-slate-400 text-[10px] uppercase tracking-wider mb-2 flex items-center gap-2">
          <Shield className="w-3 h-3" />
          Restaurantes actualmente bloqueados ({active.length})
        </h4>
        {active.length === 0 ? (
          <div className="bg-slate-800/30 rounded-lg p-4 text-center">
            <p className="text-muted-foreground text-xs">Ningún restaurante tiene throttle activo.</p>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="active-throttles-list">
            {active.map((t) => (
              <div
                key={t.throttle_id}
                className="flex items-center gap-2 text-[11px] py-2 px-3 rounded-lg border bg-rose-500/5 border-rose-500/20"
              >
                <Shield className="w-3 h-3 text-rose-400 flex-shrink-0" />
                <span className="text-slate-200 font-medium flex-1 truncate">{t.restaurant_name}</span>
                <span className="text-muted-foreground text-[10px]">
                  {t.strikes_count} strikes / {t.window_days}d
                </span>
                <span className="text-rose-400 w-28 text-right text-[10px]">
                  expira {new Date(t.expires_at).toLocaleDateString("es-ES", { day: "2-digit", month: "short" })}
                </span>
                <button
                  onClick={() => handleRelease(t.restaurant_id, t.restaurant_name)}
                  disabled={releasing === t.restaurant_id}
                  data-testid={`release-throttle-${t.restaurant_id}`}
                  className="ml-2 bg-emerald-600/20 hover:bg-emerald-600/40 text-emerald-300 text-[10px]
                             px-2 py-1 rounded-full flex items-center gap-1 transition-colors disabled:opacity-50"
                >
                  {releasing === t.restaurant_id
                    ? <Loader2 className="w-3 h-3 animate-spin" />
                    : <Unlock className="w-3 h-3" />}
                  Liberar
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function FunnelTile({ label, value, sub, color = "text-white" }) {
  return (
    <div className="bg-slate-800/50 rounded-lg p-2.5">
      <p className="text-muted-foreground text-[9px] uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-base font-bold leading-tight ${color}`}>{value}</p>
      {sub && <p className="text-muted-foreground text-[9px]">{sub}</p>}
    </div>
  );
}

function formatTTC(hours) {
  if (hours == null) return "—";
  if (hours < 1) {
    const mins = Math.round(hours * 60);
    return `${mins} min`;
  }
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${(hours / 24).toFixed(1)} d`;
}


// ── WeeklyDigestPanel: Monday email digest toggle + "Send now" ─────────
function WeeklyDigestPanel() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const c = await adminFetch("/api/admin/ai-usage/weekly-digest/config");
      setConfig(c);
    } catch {
      toast.error("Error cargando config del digest");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggleEnabled = async (next) => {
    try {
      const saved = await adminFetch("/api/admin/ai-usage/weekly-digest/config", {
        method: "PUT",
        body: JSON.stringify({ enabled: next }),
      });
      setConfig(saved);
      toast.success(next ? "Digest semanal activado" : "Digest semanal desactivado");
    } catch {
      toast.error("Error guardando config");
    }
  };

  const sendNow = async () => {
    if (!window.confirm("¿Enviar el digest IA ahora a todos los superadmins?")) return;
    setSending(true);
    try {
      const r = await adminFetch("/api/admin/ai-usage/weekly-digest/send-now", {
        method: "POST",
      });
      if (r.sent) {
        toast.success(`Digest enviado a ${r.recipients.length} admin(s)`, {
          description: `Margen: ${r.margin_eur?.toFixed(2)}€ · Conv: ${r.conversion_rate_pct}%`,
        });
        await load();
      } else {
        toast.warning("Digest no enviado", { description: r.reason });
      }
    } catch {
      toast.error("Error enviando digest");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5 flex items-center gap-3">
        <Loader2 className="w-4 h-4 text-sky-400 animate-spin" />
        <span className="text-muted-foreground text-xs">Cargando digest semanal…</span>
      </div>
    );
  }
  if (!config) return null;

  const enabled = !!config.enabled;

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5" data-testid="weekly-digest-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${enabled ? "bg-sky-500/15" : "bg-slate-800"}`}>
            <Mail className={`w-4 h-4 ${enabled ? "text-sky-400" : "text-muted-foreground"}`} />
          </div>
          <div className="min-w-0">
            <h3 className="text-white text-sm font-bold">Digest IA semanal por email</h3>
            <p className="text-muted-foreground text-[10px]">
              Lunes 08:00 UTC — snapshot de margen, embudo de throttle y top
              restaurantes en peor situación. Se envía a todos los superadmins.
            </p>
            {config.last_sent && (
              <p className="text-muted-foreground text-[10px] mt-0.5">
                Último envío: {new Date(config.last_sent).toLocaleString("es-ES", {
                  day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
                })}
                {config.last_margin_eur !== undefined && (
                  <span className="ml-2 text-muted-foreground">
                    · margen {config.last_margin_eur?.toFixed?.(2) ?? "—"}€
                    · conv {config.last_conv_rate_pct ?? "—"}%
                  </span>
                )}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={sendNow}
            disabled={sending}
            data-testid="send-digest-now-btn"
            className="bg-sky-600 hover:bg-sky-500 disabled:bg-slate-700 disabled:text-slate-500
                       text-white text-xs font-medium px-3 py-1.5 rounded-lg transition-colors flex items-center gap-2"
          >
            {sending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando…</> : <><Send className="w-3.5 h-3.5" /> Enviar ahora</>}
          </button>
          <label className="flex items-center gap-2 cursor-pointer" data-testid="toggle-weekly-digest">
            <span className="text-slate-400 text-xs">{enabled ? "ON" : "OFF"}</span>
            <button
              onClick={() => toggleEnabled(!enabled)}
              className={`relative w-9 h-5 rounded-full transition-colors ${enabled ? "bg-sky-500" : "bg-slate-700"}`}
              aria-pressed={enabled}
            >
              <span
                className={`absolute top-0.5 w-4 h-4 bg-card rounded-full shadow transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
              />
            </button>
          </label>
        </div>
      </div>
    </div>
  );
}

